// Portfolio case studies — qualitative outcomes only.
// No invented revenue numbers, no fake percentages. Outcomes are descriptive.

export const PORTFOLIO = [
  {
    slug: 'cleaning-company-operations',
    title: 'Rebuilding the operational backbone of a growing cleaning company',
    client: 'Residential cleaning company',
    industry: 'Residential Cleaning',
    year: '2025',
    hero: 'https://images.unsplash.com/photo-1719760518176-e124a5bcd025',
    summary:
      'A growing cleaning company hitting capacity. We rebuilt scheduling, client communication and onboarding so the team could deliver consistently as they grew.',
    challenge:
      'A cleaning company on a strong growth trajectory was losing inbound quotes to slow response times. The founders were stuck in day-to-day operations. Onboarding was inconsistent, the CRM had stale data, and every team member had a different version of the truth.',
    solution:
      'We mapped every operational workflow, then rebuilt the four highest-impact ones: lead response, quoting, client onboarding, and weekly team huddles. We migrated the CRM, set response-time standards, and documented every recurring task into SOPs the team could adopt without supervision.',
    tools: ['Jobber', 'Google Workspace', 'Notion', 'Slack', 'Stripe', 'Loom'],
    outcomes: [
      'Improved customer communication',
      'Streamlined scheduling processes',
      'Implemented CRM workflows',
      'Built systems that support growth',
    ],
    gallery: [
      'https://images.pexels.com/photos/7599590/pexels-photo-7599590.jpeg',
      'https://images.pexels.com/photos/13059862/pexels-photo-13059862.jpeg',
      'https://images.pexels.com/photos/8360032/pexels-photo-8360032.jpeg',
    ],
  },
  {
    slug: 'home-services-website-relaunch',
    title: 'A premium website relaunch for a multi-service home brand',
    client: 'Home services company',
    industry: 'Home Services',
    year: '2025',
    hero: 'https://images.pexels.com/photos/13059862/pexels-photo-13059862.jpeg',
    summary:
      'A respected home-services brand looked dated online. We redesigned the website and the lead flow behind it so the digital presence finally matched the quality of the work.',
    challenge:
      'The business offered four premium service lines, but the website read like an old template — scattered, hard to navigate on mobile, and underperforming on local search. Inbound calls were dropping while local competitors took share.',
    solution:
      'A complete restructure: dedicated SEO landing pages per service, a single conversion-first home page, and a streamlined contact flow that delivered inquiries directly to the operations calendar. SEO foundations and schema markup were built in from day one.',
    tools: ['Modern web stack', 'Google Search Console', 'HubSpot CRM', 'Calendly'],
    outcomes: [
      'Created a professional, mobile-friendly presence',
      'Built clear paths from visit to consultation request',
      'Strengthened local visibility on search',
      'Reduced operational bottlenecks in lead handling',
    ],
    gallery: [
      'https://images.unsplash.com/photo-1719760518176-e124a5bcd025',
      'https://images.pexels.com/photos/30715673/pexels-photo-30715673.jpeg',
    ],
  },
  {
    slug: 'wellness-studio-launch',
    title: 'Brand foundations and launch systems for a wellness studio',
    client: 'Wellness studio',
    industry: 'Wellness',
    year: '2024',
    hero: 'https://images.pexels.com/photos/8360032/pexels-photo-8360032.jpeg',
    summary:
      'Helping an experienced practitioner launch her own studio with brand, website and operations systems from day one — built to scale without breaking.',
    challenge:
      'A respected practitioner spinning out into her own studio needed everything at once: brand, website, booking system, client onboarding, and a way to handle inquiries without losing time on the table.',
    solution:
      'We delivered a complete launch package — brand voice, minimalist website, integrated booking, automated welcome sequence, and a lightweight operations dashboard. She opened her doors with a system that ran itself.',
    tools: ['Squarespace', 'Acuity', 'Notion', 'Mailerlite'],
    outcomes: [
      'Built systems that support growth',
      'Implemented CRM workflows',
      'Streamlined scheduling processes',
      'Created scalable processes',
    ],
    gallery: [
      'https://images.unsplash.com/photo-1719760518176-e124a5bcd025',
      'https://images.pexels.com/photos/7599590/pexels-photo-7599590.jpeg',
    ],
  },
  {
    slug: 'property-care-team-systems',
    title: 'Operations and team systems for a property care firm',
    client: 'Property care firm',
    industry: 'Property Services',
    year: '2025',
    hero: 'https://images.pexels.com/photos/30715673/pexels-photo-30715673.jpeg',
    summary:
      'A founder running on adrenaline. We installed the systems and ownership that let the team take the day-to-day off the owner\u2019s plate.',
    challenge:
      'The team had grown but the owner was still the bottleneck for every quote, every dispatch and every escalation. The team was capable but unclear on standards and ownership.',
    solution:
      'Role clarity, dispatch SOPs, a single source of truth for client status, and a weekly operations rhythm. We coached the founder on delegating to outcomes rather than tasks, and trained two team leads to own their workflows.',
    tools: ['Housecall Pro', 'ClickUp', 'Loom', 'Google Workspace'],
    outcomes: [
      'Reduced operational bottlenecks',
      'Clearer ownership across the team',
      'Improved customer communication',
      'Built systems that support growth',
    ],
    gallery: [
      'https://images.pexels.com/photos/13059862/pexels-photo-13059862.jpeg',
      'https://images.pexels.com/photos/8360032/pexels-photo-8360032.jpeg',
    ],
  },
];

export const getProjectBySlug = (slug) => PORTFOLIO.find((p) => p.slug === slug);
